import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Plus, Search, Download, Mail, Edit, Trash2, BellRing, RotateCcw, XCircle } from 'lucide-react';
import { formatDateShort } from '../../utils/dateFormat';
import { logger } from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const InvoicesList = () => {
  const [invoices, setInvoices] = useState([]);
  const [trash, setTrash] = useState([]);
  const [tab, setTab] = useState('active');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [reminderSendingId, setReminderSendingId] = useState(null);
  const navigate = useNavigate();
  const password = sessionStorage.getItem('admin_password');

  useEffect(() => {
    if (!password) {
      navigate('/admin/login');
      return;
    }
    fetchInvoices();
    fetchTrash();
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/invoices`, {
        headers: {
          'Authorization': `Basic ${btoa(':' + password)}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setInvoices(data);
      } else if (response.status === 401) {
        navigate('/admin/login');
      }
    } catch (err) {
      logger.error('Failed to fetch invoices:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrash = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/invoices/trash`, {
        headers: { 'Authorization': `Basic ${btoa(':' + password)}` },
      });
      if (res.ok) setTrash(await res.json());
    } catch (err) {
      logger.error('Failed to fetch trashed invoices:', err);
    }
  };

  const handleDelete = async (invoiceId) => {
    if (!window.confirm('Move this invoice to trash? You can restore it later from the Trash tab.')) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/invoices/${invoiceId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Basic ${btoa(':' + password)}`
        }
      });

      if (response.ok) {
        fetchInvoices();
        fetchTrash();
      }
    } catch (err) {
      logger.error('Failed to delete invoice:', err);
    }
  };

  const handleRestore = async (invoiceId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/invoices/${invoiceId}/restore`, {
        method: 'POST',
        headers: { 'Authorization': `Basic ${btoa(':' + password)}` },
      });
      if (response.ok) {
        fetchInvoices();
        fetchTrash();
      }
    } catch (err) {
      logger.error('Failed to restore invoice:', err);
    }
  };

  const handlePermanentDelete = async (invoiceId) => {
    if (!window.confirm('Permanently delete this invoice? This cannot be undone.')) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/invoices/${invoiceId}/permanent`, {
        method: 'DELETE',
        headers: { 'Authorization': `Basic ${btoa(':' + password)}` },
      });
      if (response.ok) fetchTrash();
    } catch (err) {
      logger.error('Failed to permanently delete invoice:', err);
    }
  };

  const handleDownloadPDF = async (invoice) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/invoices/${invoice.id}/download-pdf`, {
        headers: {
          'Authorization': `Basic ${btoa(':' + password)}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Invoice_${invoice.client_name.replace(/\s/g, '_')}_${invoice.invoice_number.replace(/\//g, '-')}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Failed to download PDF');
      }
    } catch (err) {
      logger.error('Download error:', err);
      alert('Failed to download PDF. Please try again.');
    }
  };

  const handleSendReminder = async (invoice, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm(
      `Send a payment reminder to ${invoice.client_name} (${invoice.client_email})?\n\n` +
      `Invoice ${invoice.invoice_number} — ${invoice.currency}${(invoice.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    )) return;
    setReminderSendingId(invoice.id);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/invoices/${invoice.id}/send-payment-reminder`,
        { method: 'POST', headers: { 'Authorization': `Basic ${btoa(':' + password)}` } }
      );
      if (response.ok) {
        const data = await response.json();
        alert(`Payment reminder sent to ${invoice.client_email}.\nOutstanding balance: ${invoice.currency}${(data.balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
      } else {
        const err = await response.json().catch(() => ({}));
        alert(`Failed to send reminder: ${err.detail || 'Unknown error'}`);
      }
    } catch (err) {
      logger.error('Send reminder error:', err);
      alert('Failed to send reminder. Please try again.');
    } finally {
      setReminderSendingId(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-800 border-gray-200',
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      paid: 'bg-green-100 text-green-800 border-green-200',
      overdue: 'bg-red-100 text-red-800 border-red-200',
      cancelled: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${styles[status] || styles.pending}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const activeList = tab === 'trash' ? trash : invoices;
  const filteredInvoices = activeList.filter(invoice => {
    const matchesSearch = (invoice.client_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (invoice.invoice_number || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-foreground/60">Loading invoices...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-border">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between admin-header-row">
            <div>
              <h1 className="text-2xl font-bold text-primary">Invoices</h1>
              <p className="text-sm text-foreground/60">Manage all invoices</p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-gray-50 transition-colors"
              >
                ← Dashboard
              </button>
              <button
                onClick={() => navigate('/admin/invoices/new')}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                New Invoice
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-1 border-b border-border mb-6">
          <button
            onClick={() => setTab('active')}
            data-testid="tab-active-invoices"
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${tab === 'active' ? 'border-primary text-primary' : 'border-transparent text-foreground/60 hover:text-foreground'}`}
          >
            Active <span className="ml-1 text-xs text-foreground/50">({invoices.length})</span>
          </button>
          <button
            onClick={() => setTab('trash')}
            data-testid="tab-trash-invoices"
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors flex items-center gap-1.5 ${tab === 'trash' ? 'border-primary text-primary' : 'border-transparent text-foreground/60 hover:text-foreground'}`}
          >
            <Trash2 className="w-3.5 h-3.5" /> Trash <span className="text-xs text-foreground/50">({trash.length})</span>
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-6 border border-border shadow-sm mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-foreground/40" />
              <input
                type="text"
                placeholder="Search by client name or invoice number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        {/* Invoices Table */}
        <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
          {filteredInvoices.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-foreground/20 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No invoices found</h3>
              <p className="text-foreground/60 mb-4">Create your first invoice to get started</p>
              <button
                onClick={() => navigate('/admin/invoices/new')}
                className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Create Invoice
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-border">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-foreground/60 uppercase tracking-wider">Invoice #</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-foreground/60 uppercase tracking-wider">Client</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-foreground/60 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-foreground/60 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-foreground/60 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-foreground/60 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredInvoices.map((invoice) => (
                    <tr key={invoice.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-mono text-sm font-semibold text-primary">{invoice.invoice_number}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-medium text-foreground">{invoice.client_name}</div>
                          <div className="text-sm text-foreground/60">{invoice.client_email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground/70">
                        {formatDateShort(invoice.invoice_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-semibold text-foreground">{invoice.currency}{invoice.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(invoice.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-2">
                          {tab === 'trash' ? (
                            <>
                              <button
                                onClick={() => navigate(`/admin/invoices/${invoice.id}`)}
                                className="p-2 text-foreground/60 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                                title="View"
                              >
                                <FileText className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleRestore(invoice.id)}
                                data-testid={`restore-invoice-${invoice.id}`}
                                className="px-3 py-1.5 text-xs font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-1.5"
                                title="Restore this invoice"
                              >
                                <RotateCcw className="w-3.5 h-3.5" /> Restore
                              </button>
                              <button
                                onClick={() => handlePermanentDelete(invoice.id)}
                                data-testid={`permadelete-invoice-${invoice.id}`}
                                className="p-2 text-foreground/60 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete permanently"
                              >
                                <XCircle className="w-4 h-4" />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => navigate(`/admin/invoices/${invoice.id}`)}
                                className="p-2 text-foreground/60 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                                title="View"
                              >
                                <FileText className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDownloadPDF(invoice)}
                                className="p-2 text-foreground/60 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                title="Download PDF"
                              >
                                <Download className="w-4 h-4" />
                              </button>
                              {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                                <button
                                  onClick={(e) => handleSendReminder(invoice, e)}
                                  disabled={reminderSendingId === invoice.id}
                                  data-testid={`invoice-reminder-btn-${invoice.id}`}
                                  className="px-3 py-1.5 text-xs font-semibold bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                                  title="Send payment reminder email"
                                >
                                  <BellRing className={`w-3.5 h-3.5 ${reminderSendingId === invoice.id ? 'animate-pulse' : ''}`} />
                                  {reminderSendingId === invoice.id ? 'Sending…' : 'Remind'}
                                </button>
                              )}
                              <button
                                onClick={() => navigate(`/admin/invoices/${invoice.id}/edit`)}
                                className="p-2 text-foreground/60 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Edit"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(invoice.id)}
                                className="p-2 text-foreground/60 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Move to trash"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};